import {
  PAYMENTS_EXPERIMENT_NAME,
  PAYMENTS_STEPS,
  V2_CAPACITY_ERROR_TYPES,
  BillStatus,
} from '@/data/payments';
import { RouteNames } from '@/data/routes';
import {
  SelfRevenueRanges,
  SelfRevenueRangeToSelfReportedCapacityCents,
} from '@/data/self-revenue-range-offers';
// reusing helpers from onboarding module
import {
  isBankLinkConfirmationStepComplete,
  isBizProfileBusinessDetailsStepComplete,
  isBusinessFormationDocsStepComplete,
  isConnectBankingStepComplete,
  isOwnersComplete,
  isOwnersListStepComplete,
  isOwnersStepOneComplete,
  isUploadBankStatementsStepComplete,
} from '@/utils/onboarding-steps';
import { paymentsRequest } from '@/utils/payments-request';
import { beneficiaryRequest } from '@/utils/beneficiary-request';
import { buildApiActions } from '@/utils/vuex-api-utils';
import { stateOptionsForCountry } from '@/utils/local';
import { CAPACITY, ENTITY_TYPE } from '@clearbanc/data-common-types';
import { LocalStorage, LocalStorageKeys } from '@/utils/local-storage';
import {
  CAPACITY_TYPES,
  STRATEGY_TYPES,
  MINIMUM_CAPACITY_CENTS_FOR_ROLLING_FUNDING,
  CAPACITY_STATUSES,
} from '@/data/capacity';
import { invitedUserOnboardingSteps } from '@/data/user-invite';
import { isAddressComplete, hasRequiredIdentification } from './profile-module';
import router from '@/routes';
import { BillType } from '@/@types/bill.types';
import { parseBillSchema } from '@/utils/bill-schema';
import { areDocumentUploadsHidden } from '@/utils/document-upload-requirements';
import { getOnboardingCompletionRoute } from '@/utils/router-helper';
import { addDays, isAfter } from 'date-fns';

const CAPACITY_BOTTOM_OUT_CENTS = 100000; // $1000.00

const MAX_DEFAULT_BILL_AMOUNT_CENTS = 1000000000; // $10M

export function isUserProfileStepComplete(user) {
  return !!(user.firstName && user.lastName && user.phoneNumber);
}

export function isOnboardingBusinessDetailsComplete(business) {
  const isMonthlyRevInList = Object.values(SelfRevenueRanges).includes(
    business?.qualifyingQuestions?.monthly_revenue,
  );

  return !!(
    business.corpCountry &&
    (business.corpState || !stateOptionsForCountry(business.corpCountry)) &&
    business.qualifyingQuestions?.monthly_revenue &&
    isMonthlyRevInList &&
    business.name &&
    business.website
  );
}

export function isRegisteredAddressComplete(business) {
  return !!(
    business.address?.line1 &&
    business.address?.city &&
    business.address?.country &&
    (business.address?.state ||
      !stateOptionsForCountry(business.address?.country)) &&
    business.address?.postalCode
  );
}

export function isBusinessProfileCompleteFn(rootGetters) {
  return !!(
    rootGetters.bankingAccountsConnected &&
    rootGetters.isBusinessDetailsStepComplete &&
    rootGetters.isBusinessOwnersStepComplete &&
    (!rootGetters.debtObligationsRequired ||
      rootGetters.businessDebtObligations?.id)
  );
}

const businessProfileSteps = {
  [RouteNames.BUSINESS.INDEX]: {
    isComplete: false,
    isDisabled: true,
    title: 'Business Details',
    label: 'Add Business Details',
    name: RouteNames.BUSINESS.INDEX,
  },
  [RouteNames.OWNERS.INDEX]: {
    isComplete: false,
    isDisabled: true,
    title: 'Business Owners/Directors Profiles',
    label: 'Add Business Owner Profiles',
    name: RouteNames.OWNERS.INDEX,
  },
  [RouteNames.BANKING.INDEX]: {
    isComplete: false,
    isDisabled: true,
    title: 'Set Up Electronic Fund Transfer',
    subtitle: 'We automate debits so you never need to worry about it',
    label: 'Bank Accounts',
    name: RouteNames.BANKING.INDEX,
  },
  [RouteNames.DEBT_OBLIGATIONS.INDEX]: {
    isComplete: false,
    isDisabled: true,
    title: 'Title',
    subtitle: 'Subtitle',
    label: 'Financial Details & Obligations',
    name: RouteNames.DEBT_OBLIGATIONS.INDEX,
  },
};

export default {
  state: {
    experimentName: PAYMENTS_EXPERIMENT_NAME,
    paymentsOnboardingSteps: {
      [RouteNames.AUTH.SIGNUP]: {
        name: RouteNames.AUTH.SIGNUP,
        nextRoute: { name: RouteNames.ONBOARDING.USER },
        isComplete: false,
        title: 'Sign up',
      },
      [RouteNames.ONBOARDING.USER]: {
        title: 'Profile Details',
        name: RouteNames.ONBOARDING.USER,
        nextRoute: { name: RouteNames.ONBOARDING.BUSINESS },
        isComplete: false,
        percentComplete: 25,
      },
      [RouteNames.ONBOARDING.BUSINESS]: {
        name: RouteNames.ONBOARDING.BUSINESS,
        title: 'Business Details',
        nextRoute: { name: RouteNames.ONBOARDING.ADDRESS },
        isComplete: false,
        percentComplete: 50,
        previousRoute: { name: RouteNames.ONBOARDING.USER },
      },
      [RouteNames.ONBOARDING.ADDRESS]: {
        name: RouteNames.ONBOARDING.ADDRESS,
        title: 'Address',
        subtitle: 'We need this info to review and verify your account',
        nextRoute: { name: RouteNames.HOME.INITIAL },
        isComplete: false,
        percentComplete: 75,
        previousRoute: { name: RouteNames.ONBOARDING.BUSINESS },
      },
      [RouteNames.ONBOARDING.REVENUE_ACCOUNT]: {
        name: RouteNames.ONBOARDING.REVENUE_ACCOUNT,
        title: 'Connect Revenue Account',
        subtitle:
          'Connect at least one e-commerce sales platform to verify your revenue and determine your funding capacity. The more accounts you connect, the better we can understand your business and help pay more bills.',
        subtitleCta: {
          link: 'Learn more.',
          href: 'https://www.clear.co/case-studies',
        },
        nextRoute: {
          name: RouteNames.PROFILE.INDEX,
        },
        isComplete: false,
        percentComplete: 90,
        previousRoute: { name: RouteNames.ONBOARDING.ADDRESS },
      },
    },
    invitedUserOnboardingSteps,
    businessProfileSteps,
    steps: PAYMENTS_STEPS,
    billsTotalCount: 0,
    bills: [],
    selectedBill: {},
    createdBill: {},
    rates: [],
    advertisedRates: {},
    repaymentSchedule: [],
    scheduledTransactions: [],
    beneficiaries: [],
    v2Beneficiaries: {},
    filteredRepayments: {},
    transactionsSummary: {},
    businessCapacity: {},
    selectedBillBeneficiary: {},
    csvDownload: null,
    billFlowType: BillType.INVOICE,
    beneficiaryFormInfo: {},
    emailVerified: false,
    billSchema: {
      // this default schema is only used if there's a network error on the first schema request
      [BillType.INVOICE]: {
        amountCents: { min: 0, max: MAX_DEFAULT_BILL_AMOUNT_CENTS },
      },
      [BillType.RECEIPT]: {
        amountCents: { min: 0, max: MAX_DEFAULT_BILL_AMOUNT_CENTS },
      },
      [BillType.CASH_ADVANCE]: {
        amountCents: { min: 0, max: MAX_DEFAULT_BILL_AMOUNT_CENTS },
      },
    },
  },
  getters: {
    beneficiaryFormInfo: (state) => state.beneficiaryFormInfo,
    paymentsOnboardingSteps: (state, getters) => {
      const businessProfileSteps = getters.businessProfileSteps;
      const nextRouteName = getOnboardingCompletionRoute(businessProfileSteps);
      return {
        ...state.paymentsOnboardingSteps,
        [RouteNames.ONBOARDING.REVENUE_ACCOUNT]: {
          ...state.paymentsOnboardingSteps[
            RouteNames.ONBOARDING.REVENUE_ACCOUNT
          ],
          nextRoute: { name: nextRouteName },
        },
      };
    },
    invitedUserOnboardingSteps: (state) => state.invitedUserOnboardingSteps,
    isEmailVerified: (state) => state.emailVerified,
    businessProfileSteps: (state, getters, rootState, rootGetters) => {
      const computedSteps = { ...state.businessProfileSteps };
      delete computedSteps[RouteNames.CONNECTED_ACCOUNTS.INDEX];
      return computedSteps;
    },
    steps: (state) => state.steps,
    firstIncompletePaymentsOnboardingStep: (state, getters) => {
      const incompleteStep = Object.values(
        getters.paymentsOnboardingSteps,
      ).find((val) => !val.isComplete);
      // If all steps are complete, redirect to payments dashboard
      if (!incompleteStep) {
        return { name: RouteNames.HOME.INITIAL };
      }
      return incompleteStep;
    },
    firstIncompleteInvitedUserOnboardingStep: (state, getters) => {
      const incompleteStep = Object.values(
        getters.invitedUserOnboardingSteps,
      ).find((step) => !step.isComplete);
      // If all steps are complete, redirect to payments dashboard
      return incompleteStep ?? { name: RouteNames.HOME.INITIAL };
    },
    firstIncompleteOnboardingStep: (state, getters, rootState, rootGetters) =>
      rootGetters.user.isPrimary
        ? getters.firstIncompletePaymentsOnboardingStep
        : getters.firstIncompleteInvitedUserOnboardingStep,
    beneficiaries: (state) => state.beneficiaries,
    billsTotalCount: (state) => state.billsTotalCount,
    bills: (state) => state.bills,
    maxCapacityExceeded: (state, getters) => {
      return (
        getters.businessCapacityGenerated &&
        Number(state.businessCapacity.usedCents) >
          Number(state.businessCapacity.totalCents)
      );
    },
    isManualUnderwriting: (state, getters) => {
      return getters.businessCapacity?.isManualUnderwriting;
    },
    scheduledTransactions: (state) => state.scheduledTransactions,
    repaymentSchedule: (state) => state.repaymentSchedule,
    advertisedRates: (state) => state.advertisedRates,
    selectedBill: (state) => state.selectedBill,
    createdBill: (state) => state.createdBill,
    isConnectBankingStepComplete: (state, getters, rootState, rootGetters) =>
      isConnectBankingStepComplete(rootGetters),
    isBankLinkConfirmationStepComplete: (
      state,
      getters,
      rootState,
      rootGetters,
    ) => isBankLinkConfirmationStepComplete(rootGetters),
    isBusinessDetailsStepComplete: (state, getters, rootState, rootGetters) =>
      isBizProfileBusinessDetailsStepComplete(
        rootGetters.business,
        rootGetters,
      ),
    isBusinessOwnersStepComplete: (state, getters, rootState, rootGetters) =>
      isOwnersComplete(getters),
    isUploadBankStatementsStepComplete: (
      state,
      getters,
      rootState,
      rootGetters,
    ) => isUploadBankStatementsStepComplete(rootGetters),
    filteredRepayments: (state) => state.filteredRepayments,
    transactionsSummary: (state) => state.transactionsSummary,
    experimentName: (state) => state.experimentName,
    bankingAccountsConnected: (state, getters, rootState, rootGetters) =>
      (rootGetters.isConnectBankingStepComplete &&
        rootGetters.isBankLinkConfirmationStepComplete) ||
      (rootGetters.isUploadBankStatementsStepComplete &&
        rootGetters.businessOnPlaidBypass),
    businessCapacity: (state, getters, rootState, rootGetters) => {
      if (!state.businessCapacity) {
        return undefined;
      }

      return {
        ...state.businessCapacity,
        totalCents: Number(state.businessCapacity.totalCents),
        usedCents: Number(state.businessCapacity.usedCents),
        availableCents: Number(state.businessCapacity.availableCents),
      };
    },
    businessCapacityGenerated: (state) =>
      Object.keys(state.businessCapacity).length > 0,
    businessCapacityApproved: (state) => !!state.businessCapacity?.approvedAt,
    isApprovedRollingCapacity: (state, rootGetters) => {
      return (
        rootGetters.businessCapacityApproved &&
        state.businessCapacity.exposureStrategy ===
          STRATEGY_TYPES.prorata_origination
      );
    },
    isApprovedStaticFundingCapacityV2: (state, rootGetters) => {
      return (
        rootGetters.businessCapacityApproved &&
        state.businessCapacity.exposureStrategy ===
          STRATEGY_TYPES.static_origination_v2
      );
    },
    capacityType: (state, getters) => {
      switch (true) {
        case getters.isApprovedRollingCapacity:
          return CAPACITY_TYPES.rolling_funding;
        case getters.isApprovedStaticFundingCapacityV2:
          return CAPACITY_TYPES.fixed_funding;
        default:
          return CAPACITY_TYPES.funding;
      }
    },
    selectedBillBeneficiary: (state) => state.selectedBillBeneficiary,
    previousPaymentsStepName: (state, getters) => (currentStepName) => {
      const stepNames = Object.keys(getters.steps);
      return stepNames[stepNames.indexOf(currentStepName) - 1];
    },
    isBusinessProfileComplete: (state, rootGetters) =>
      isBusinessProfileCompleteFn(rootGetters),
    isBnplWaitlisted: (state, getters, rootState, rootGetters) =>
      // additional logic can be added here, such as per-user waitlisting
      !rootGetters.isUSBusiness && !rootGetters.userHasForceBnplFlag,

    userCanSubmitInvoices: (state, getters, rootState, rootGetters) => {
      // Cases for not being able to submit an invoice:
      // case 1: User has low rev = self-reported revenue under 10k AND capaciy is not generated yet
      // case 2: Remaining capacity is under 1k
      // case 3: backend returns disqualified
      // case 4: User has an unsettled MCA and has previous BILL advances and the BNPL force flag is FALSE
      // case 5: user does not have a connected primary bank account and capacity is calculated (behind feat flag)
      // case 6: capacity has not been calculated AND bill submission before verifying revenue is disabled
      // case 7: bill submission before verifying revenue is enabled AND business profile is not complete
      // case 8: bill submission before verifying revenue is enabled AND remaining self-reported capacity is <= 0 AND capacity is not generated
      const capacityRequest = rootGetters.requestStatus(
        'GET_BUSINESS_CAPACITY',
      );

      const selfReportedCapacityUnder10k =
        rootGetters.business.qualifyingQuestions?.monthly_revenue ===
        SelfRevenueRanges.from_0_to_10;

      const availableCents = getters.businessCapacity.availableCents;

      const businessCapacityUnder1k =
        availableCents <= CAPACITY_BOTTOM_OUT_CENTS;

      const disqualifiedError =
        V2_CAPACITY_ERROR_TYPES.CAPACITY_DISQUALIFIED ===
        capacityRequest?.error?.type;

      const disconnectedPrimaryBankAccount =
        rootGetters.isPrimaryBankAccountDisconnected;

      return !(
        // case 1
        (
          (selfReportedCapacityUnder10k &&
            capacityRequest?.error?.type ===
              V2_CAPACITY_ERROR_TYPES.CAPACITY_NOT_FOUND) ||
          // case 2
          (capacityRequest.isSuccess && businessCapacityUnder1k) ||
          // case 3
          disqualifiedError ||
          // case 4
          (rootGetters.userHasActiveValidAdvances &&
            !rootGetters.userHasSettledValidAdvances &&
            rootGetters.userHasPreviousBillAdvances &&
            !rootGetters.userHasForceBnplFlag) ||
          // case 5
          (disconnectedPrimaryBankAccount && capacityRequest.isSuccess) ||
          // case 7
          !getters.isBusinessProfileComplete ||
          // case 8
          (getters.remainingSelfReportedCapacityCents <= 0 &&
            !getters.businessCapacityGenerated)
        )
      );
    },
    currentCapacityStatus: (state, getters) => {
      if (!getters.businessCapacityGenerated) {
        return CAPACITY_STATUSES.self_reported;
      }

      return getters.businessCapacityApproved
        ? CAPACITY_STATUSES.approved
        : CAPACITY_STATUSES.preliminary;
    },
    businessEligibleForRollingFunding: (state, getters) => {
      const capacityStatus = getters.currentCapacityStatus;
      const minimumCapacity = MINIMUM_CAPACITY_CENTS_FOR_ROLLING_FUNDING;

      if (getters.userIsDisqualified) {
        return false;
      }

      if (capacityStatus === CAPACITY_STATUSES.self_reported) {
        return getters.selfReportedCapacityCents >= minimumCapacity;
      }

      if (
        [CAPACITY_STATUSES.preliminary, CAPACITY_STATUSES.approved].includes(
          capacityStatus,
        )
      ) {
        return state.businessCapacity.totalCents >= minimumCapacity;
      }

      return false;
    },
    businessCanSubmitRollingFundingInquiry: (
      state,
      getters,
      rootState,
      rootGetters,
    ) => {
      const hasRollingCapacity =
        state.businessCapacity?.exposureStrategy ===
        STRATEGY_TYPES.prorata_origination;

      if (!getters.businessEligibleForRollingFunding || hasRollingCapacity) {
        return false;
      }

      if (getters.currentCapacityStatus === CAPACITY_STATUSES.approved) {
        const oneWeekFromNow = addDays(new Date(), 7);
        const capacityExpiryDate = state.businessCapacity.expiresAt
          ? new Date(state.businessCapacity.expiresAt)
          : null;

        // Return false if capacity expiry is more than 1 week away
        if (capacityExpiryDate && isAfter(capacityExpiryDate, oneWeekFromNow)) {
          return false;
        }
      }

      return true;
    },
    hasRevenueUnder10k: (state, getters, rootState, rootGetters) => {
      return (
        rootGetters.business?.qualifyingQuestions?.monthly_revenue ===
        SelfRevenueRanges.from_0_to_10
      );
    },
    userIsDisqualified: (state, getters, rootState, rootGetters) => {
      const selfReportedCapacityUnder10k = getters.hasRevenueUnder10k;

      const capacityRequest = rootGetters.requestStatus(
        'GET_BUSINESS_CAPACITY',
      );

      const errorType = capacityRequest?.error?.type;

      return (
        !rootGetters.isUSBusiness ||
        [
          V2_CAPACITY_ERROR_TYPES.CAPACITY_DISQUALIFIED,
          V2_CAPACITY_ERROR_TYPES.DISCONNECTED_ECOM,
        ].includes(errorType) ||
        (selfReportedCapacityUnder10k &&
          errorType === V2_CAPACITY_ERROR_TYPES.CAPACITY_NOT_FOUND)
      );
    },
    selfReportedCapacityCents: (_state, _getters, _rootState, rootGetters) =>
      rootGetters.business?.qualifyingQuestions?.monthly_revenue
        ? SelfRevenueRangeToSelfReportedCapacityCents[
            rootGetters.business.qualifyingQuestions.monthly_revenue
          ]
        : 0,
    remainingSelfReportedCapacityCents: (state, getters) => {
      const totalUploadedBillAmountCents = state.bills
        .filter((bill) => bill.status !== BillStatus.CANCELLED)
        .reduce((acc, bill) => acc + parseInt(bill.amountCents), 0);
      return getters.selfReportedCapacityCents - totalUploadedBillAmountCents;
    },
    maxCapacityOverThreshold: (_state, getters, rootState, rootGetters) => {
      if (getters.businessCapacityGenerated) {
        const capacityCents = getters.businessCapacity?.totalCents;
        return capacityCents >= CAPACITY.DOCUMENT_THRESHOLD;
      }
      return getters.selfReportedCapacityCents >= CAPACITY.DOCUMENT_THRESHOLD;
    },
    csvDownload: (state) => state.csvDownload,
    billFlowType: (state) => state.billFlowType,
    isReceiptFlow: (state) => state.billFlowType === BillType.RECEIPT,
    isInvoiceFlow: (state) => state.billFlowType === BillType.INVOICE,
    isCashAdvanceFlow: (state) => state.billFlowType === BillType.CASH_ADVANCE,
    showNewProductBanner: () =>
      !LocalStorage.getItem(LocalStorageKeys.HIDE_PHOENIX_INTRO_BANNER),
    v2Beneficiaries: (state) => Object.values(state.v2Beneficiaries),
    getV2BeneficiaryById: (state) => (id) => state.v2Beneficiaries[id],
    isMissingRevenueAccountConnection: (
      state,
      getters,
      rootState,
      rootGetters,
    ) => {
      const hasCompletedAtLeastOneBusinessProfileStep = Object.keys(
        state.businessProfileSteps,
      ).some((step) => state.businessProfileSteps[step].isComplete);

      return (
        !getters.isManualUnderwriting &&
        hasCompletedAtLeastOneBusinessProfileStep &&
        (!rootGetters.salesAccountsConnected ||
          rootGetters.hasDisconnectedSalesAccounts)
      );
    },
    isHome: (state, getters, rootState) => {
      return router.currentRoute.value.name === RouteNames.HOME.INITIAL;
    },
    zeroCapacity: (state, getters, rootState) => {
      return (
        getters.businessCapacity?.totalCents === 0 &&
        (getters.businessCapacity?.availableCents ?? 0) === 0
      );
    },
    billSchema: (state) => state.billSchema,
  },
  ...buildApiActions(
    {
      GET_BILL_SCHEMA: {
        action: (ctx) => {
          return {
            method: 'get',
            interceptor: paymentsRequest,
            url: '/bills/schema',
            afterFailure: () => {
              Sentry.captureMessage('Unable to fetch Bill Schema', 'warning');
            },
          };
        },
        mutation: (state, { response }) => {
          try {
            state.billSchema = parseBillSchema(response);
          } catch (error) {
            // In case of error, either the last successful schema or safe default will be used
            Sentry.captureMessage(
              `Unable to parse Bill Schema: ${error}`,
              'warning',
            );
          }
        },
      },
      CREATE_PAY_INVOICE: {
        action: (ctx, payload) => {
          return {
            method: 'post',
            interceptor: paymentsRequest,
            url: `/business/${ctx.rootGetters.user.businessId}/bills`,
            params: payload,
          };
        },
        mutation: (state, { response }) => {
          state.createdBill = response;
        },
      },
      CANCEL_INVOICE: {
        action: (ctx, payload) => {
          return {
            method: 'post',
            interceptor: paymentsRequest,
            url: `/bills/${payload.billId}/cancel`,
            params: payload,
          };
        },
      },
      DOWNLOAD_PAYMENTS_CSV: {
        action: (ctx, payload) => {
          return {
            method: 'get',
            url: `/businesses/${ctx.rootGetters.user.businessId}/export-bills-transactions`,
            params: payload,
          };
        },
        mutation: (state, { response }) => {
          const csvBlob = new Blob([response]);
          state.csvDownload = csvBlob;
        },
      },
      GET_BILLS: {
        action: (ctx) => {
          return {
            method: 'get',
            interceptor: paymentsRequest,
            url: `/business/${ctx.rootGetters.user.businessId}/bills`,
            params: {
              limit: 200,
            },
          };
        },
        mutation: (state, { response, responseTotalCount }) => {
          state.billsTotalCount = parseInt(responseTotalCount) ?? 0;
          state.bills = response;
        },
      },
      GET_BILL: {
        action: (ctx, payload) => {
          return {
            method: 'get',
            interceptor: paymentsRequest,
            url: `/bills/${payload.id}`,
            returnResponse: true,
            afterFailure: () => {
              ctx.dispatch('RESET_SELECTED_BILL');
            },
          };
        },
        mutation: (state, { response }) => {
          state.selectedBill = response;
          const updatedBillIndex = state.bills.findIndex(
            (bill) => bill.id === response.id,
          );
          if (updatedBillIndex >= 0) {
            state.bills[updatedBillIndex] = {
              ...response,
              beneficiaryName: state.bills[updatedBillIndex].beneficiaryName,
            };
          } else {
            state.bills.push(response);
          }
        },
      },
      UPDATE_BILL: {
        action: (ctx, payload) => {
          return {
            method: 'PATCH',
            interceptor: paymentsRequest,
            url: `/bills/${ctx.rootGetters.selectedBill.id}`,
            params: payload,
          };
        },
        mutation: (state, { response }) => {
          state.selectedBill = response;
          const updatedBillIndex = state.bills.findIndex(
            (bill) => bill.id === response.id,
          );
          if (updatedBillIndex >= 0) {
            state.bills[updatedBillIndex] = response;
          } else {
            state.bills.push(response);
          }
        },
      },
      GET_ADVERTISED_FEES: {
        action: (ctx) => {
          return {
            method: 'get',
            interceptor: paymentsRequest,
            url: `/fees/latest`,
          };
        },
        mutation: (state, { response }) => {
          state.advertisedRates = response;
        },
      },
      GET_FEES_SCHEDULE: {
        action: (ctx, payload) => {
          return {
            method: 'get',
            interceptor: paymentsRequest,
            url: `/fees/bnpl-schedule`,
            params: payload,
          };
        },
        mutation: (state, { response }) => {
          state.repaymentSchedule = response;
        },
      },
      GET_TRANSACTIONS_BY_BILL_ID: {
        action: (ctx, payload) => {
          return {
            method: 'get',
            interceptor: paymentsRequest,
            url: `/bills/${payload.billId}/scheduled-transactions`,
            params: payload,
          };
        },
        mutation: (state, { response }) => {
          state.scheduledTransactions = response;
        },
      },
      GET_TRANSACTIONS_WITH_FILTERS: {
        action: (ctx, payload) => {
          const { repaymentsType } = payload;
          const url =
            repaymentsType === 'upcoming'
              ? `/businesses/${payload.businessId}/repayment-schedule`
              : `/businesses/${payload.businessId}/repayment-history`;
          return {
            method: 'get',
            url,
            params: payload,
          };
        },
        mutation: (state, { response }) => {
          state.filteredRepayments = response;
        },
      },
      GET_TRANSACTIONS_SUMMARY: {
        action: (ctx, payload) => {
          return {
            method: 'get',
            url: `/businesses/${payload.businessId}/repayment-summary`,
            params: payload,
          };
        },
        mutation: (state, { response }) => {
          state.transactionsSummary = response;
        },
      },
      SIGN_BNPL_CONTRACT_FORM: {
        action: (ctx, payload) => ({
          method: 'patch',
          interceptor: paymentsRequest,
          url: `/bills/${payload.billId}/contract/resume-signing-session`,
          params: { returnURL: payload.returnURL.split('?')[0] },
          options: { timeout: 60000 },
          returnResponse: true,
        }),
      },
      // This will be removed when V0 Beneficiaries are deprecated
      GET_BILL_BENEFICIARIES: {
        action: (ctx) => {
          return {
            method: 'get',
            interceptor: paymentsRequest,
            url: `/business/${ctx.rootGetters.user.businessId}/beneficiaries`,
          };
        },
        mutation: (state, { response }) => {
          state.beneficiaries = response;
        },
      },
      GET_BENEFICIARIES: {
        action: (ctx, params) => {
          return {
            method: 'get',
            interceptor: beneficiaryRequest,
            url: '/v2/beneficiaries',
            params,
            returnResponse: true,
          };
        },
        mutation: (state, { response }) => {
          const sorted = response.sort(
            (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
          );
          state.beneficiaries = sorted;
        },
      },
      CREATE_BENEFICIARY: {
        action: (ctx, payload) => {
          return {
            method: 'post',
            interceptor: paymentsRequest,
            url: '/v2/beneficiaries',
            params: payload,
            returnResponse: true,
          };
        },
      },
      UPDATE_V2_BENEFICIARY: {
        action: (ctx, payload) => {
          const { beneficiaryId, ...params } = payload;
          return {
            method: 'put',
            interceptor: paymentsRequest,
            url: `/v2/beneficiaries/${beneficiaryId}`,
            params,
            returnResponse: true,
          };
        },
      },
      GET_V2_BENEFICIARY: {
        action: (ctx, payload) => {
          const { beneficiaryV2Id } = payload;
          return {
            method: 'get',
            interceptor: beneficiaryRequest,
            url: `/v2/beneficiaries/${beneficiaryV2Id}`,
            returnResponse: true,
          };
        },
        mutation: (state, { response }) => {
          state.v2Beneficiaries[response.id] = response;
        },
      },
      GET_BUSINESS_CAPACITY: {
        action: (ctx) => {
          return {
            method: 'get',
            url: `/v2/capacity`,
            params: {
              businessId: ctx.rootGetters.user.businessId,
            },
            returnResponse: true,
          };
        },
        mutation: (state, { response }) => {
          state.businessCapacity = response;
        },
      },
      GET_BENEFICIARY_BY_ID: {
        action: (ctx, payload) => ({
          method: 'get',
          interceptor: paymentsRequest,
          url: `/beneficiaries/${payload.beneficiaryId}`,
          returnResponse: true,
        }),
        mutation: (state, response) => {
          state.selectedBillBeneficiary = response.response;
        },
      },
      UPDATE_BENEFICIARY_BY_BILL_ID: {
        action: (ctx, payload) => ({
          method: 'put',
          interceptor: paymentsRequest,
          url: `/beneficiary/${payload.billId}`,
          params: payload,
          returnResponse: true,
        }),
        mutation: (state, response) => {
          state.selectedBillBeneficiary = response.response;
        },
      },
      GET_BANK_DETAILS_FIELDS: {
        action: (ctx, payload) => ({
          method: 'get',
          interceptor: beneficiaryRequest,
          url: '/payment-scheme',
          params: payload,
          returnResponse: true,
        }),
      },
    },
    {
      actions: {
        UPDATE_ALL_PAYMENTS_BIZ_PROFILE_STEPS: ({ state, dispatch }) => {
          Object.keys(state.businessProfileSteps).forEach((step) =>
            dispatch('UPDATE_PAYMENTS_BIZ_PROFILE_STEP', step),
          );
        },
        UPDATE_ALL_PAYMENTS_ONBOARDING_STEPS: ({ state, dispatch }) => {
          Object.keys(state.paymentsOnboardingSteps).forEach((step) =>
            dispatch('UPDATE_PAYMENTS_ONBOARDING_STEP', step),
          );
        },
        UPDATE_ALL_INVITED_USER_ONBOARDING_STEPS: ({ state, dispatch }) => {
          Object.keys(state.invitedUserOnboardingSteps).forEach((step) =>
            dispatch('UPDATE_INVITED_USER_ONBOARDING_STEP', step),
          );
        },
        UPDATE_INVITED_USER_ONBOARDING_STEP: (
          { state, commit, getters, rootGetters },
          step,
        ) => {
          function findCompleted(currentStep) {
            switch (currentStep) {
              case RouteNames.ONBOARDING.INVITED.USER_DETAILS: {
                const hasEssentialUserInfo = isUserProfileStepComplete(
                  rootGetters.user,
                );

                // If essential user info (first name, last name, phone number) isn't present,
                // this step cannot be complete.
                if (!hasEssentialUserInfo) {
                  return false;
                }

                // This step is complete if the user has essential info and has provided a job title,
                // OR if the Verification and ID steps are complete (in which case job title is not strictly required).
                const hasJobTitle = !!rootGetters.user.jobTitle;
                const isVerificationStepComplete = findCompleted(
                  RouteNames.ONBOARDING.INVITED.VERIFICATION,
                );
                const isIdStepComplete = findCompleted(
                  RouteNames.ONBOARDING.INVITED.ID,
                );

                return (
                  hasJobTitle ||
                  (isVerificationStepComplete && isIdStepComplete)
                );
              }
              case RouteNames.ONBOARDING.INVITED.VERIFICATION:
                return (
                  isAddressComplete(rootGetters.user.address) &&
                  hasRequiredIdentification(rootGetters.user) &&
                  !!rootGetters.user.birthday
                );
              case RouteNames.ONBOARDING.INVITED.ID:
                if (
                  areDocumentUploadsHidden({
                    isLexisNexisDDPEnabled: rootGetters.isLexisNexisDDPEnabled,
                    businessVerification: rootGetters.businessVerification,
                    businessUploads: rootGetters.uploads,
                  })
                ) {
                  return true;
                }

                return (
                  rootGetters.uploadsMatching({
                    entity: [ENTITY_TYPE.USER, ENTITY_TYPE.OWNER],
                    isGovtID: true,
                    metaId: rootGetters.user.id.toString(),
                  }).length > 0
                );
              case RouteNames.ONBOARDING.INVITED.REVENUE_ACCOUNT:
                return true;
              default:
                return false;
            }
          }
          const isComplete = findCompleted(step);
          const updatedStep = {
            ...state.invitedUserOnboardingSteps[step],
            isComplete,
            ...(step === RouteNames.ONBOARDING.INVITED.USER_DETAILS && {
              title: `Welcome to the team of ${rootGetters.business.name}`,
            }),
          };
          commit('UPDATE_INVITED_USER_ONBOARDING_STEP', updatedStep);
        },
        UPDATE_PAYMENTS_ONBOARDING_STEP: (
          { state, commit, rootGetters },
          step,
        ) => {
          function findCompleted(currentStep) {
            switch (currentStep) {
              case RouteNames.AUTH.SIGNUP:
                return true; // TODO: is sign up step complete?
              case RouteNames.ONBOARDING.USER:
                return isUserProfileStepComplete(rootGetters.user);
              case RouteNames.ONBOARDING.BUSINESS:
                return isOnboardingBusinessDetailsComplete(
                  rootGetters.business,
                );
              case RouteNames.ONBOARDING.ADDRESS:
                return isRegisteredAddressComplete(rootGetters.business);
              case RouteNames.ONBOARDING.REVENUE_ACCOUNT:
                return true; // This step is optional and can be skipped
              default:
                return false;
            }
          }
          const updatedStep = {
            ...state.paymentsOnboardingSteps[step],
            isComplete: findCompleted(step),
          };

          commit('UPDATE_PAYMENTS_ONBOARDING_STEP', updatedStep);
        },
        UPDATE_PAYMENTS_BIZ_PROFILE_STEP: (
          { state, commit, getters, rootGetters },
          step,
        ) => {
          function findCompleted(currentStep) {
            switch (currentStep) {
              case RouteNames.PROFILE.INDEX:
                return rootGetters.isBusinessProfileComplete;
              case RouteNames.CONNECTED_ACCOUNTS.INDEX:
                return (
                  rootGetters.hasActiveSalesAccounts &&
                  !rootGetters.userHasActiveBillsAdvanceAndDisconnectedSalesAccount
                );
              case RouteNames.BANKING.INDEX:
                return (
                  (getters.bankingAccountsConnected &&
                    !rootGetters.businessOnPlaidBypass) ||
                  (getters.isUploadBankStatementsStepComplete &&
                    rootGetters.businessOnPlaidBypass)
                );
              case RouteNames.BUSINESS.INDEX:
                return getters.isBusinessDetailsStepComplete;
              case RouteNames.OWNERS.INDEX:
                return rootGetters.isBusinessOwnersStepComplete;
              case RouteNames.DEBT_OBLIGATIONS.INDEX: {
                if (!rootGetters.debtObligationsRequired) {
                  return true;
                }

                return false;
              }
              default:
                return false;
            }
          }

          function findEnabled(currentStep) {
            switch (currentStep) {
              case RouteNames.OWNERS.INDEX:
                return getters.isBusinessDetailsStepComplete;
              default:
                return true;
            }
          }

          const updatedStep = {
            ...state.businessProfileSteps[step],
            isComplete: findCompleted(step),
            isDisabled: !findEnabled(step),
          };

          commit('UPDATE_PAYMENTS_BIZ_PROFILE_STEP', updatedStep);
        },
        UPDATE_PAYMENTS_STEP: (
          { state, commit, getters, rootGetters },
          step,
        ) => {
          // action to update substeps (getters.steps) within the payments-module
          function findCompleted(currentStep) {
            switch (currentStep) {
              case RouteNames.USER.INDEX:
                return isOwnersStepOneComplete(
                  rootGetters.allBusinessProfiles,
                  rootGetters.uploads,
                );
              case RouteNames.OWNERS.INDEX:
                return (
                  isOwnersListStepComplete(
                    rootGetters.user,
                    rootGetters.authorizedSignatory,
                  ) && isBusinessFormationDocsStepComplete(rootGetters)
                );
              default:
                return false;
            }
          }
          const updatedStep = {
            ...state.steps[step],
            isComplete: findCompleted(step),
          };

          commit('UPDATE_PAYMENTS_STEP', updatedStep);
        },
        RESET_SELECTED_BILL: ({ commit }) => {
          commit('RESET_SELECTED_BILL');
        },
        UPDATE_SELECTED_BILL: ({ commit }, updatedBill) => {
          commit('UPDATE_SELECTED_BILL', updatedBill);
        },
        UPDATE_BILL_FLOW_TYPE: ({ commit }, value) => {
          commit('UPDATE_BILL_FLOW_TYPE', value);
        },
        RESET_FILTERED_REPAYMENTS: ({ commit }) => {
          commit('RESET_FILTERED_REPAYMENTS');
        },
      },
      mutations: {
        UPDATE_PAYMENTS_BIZ_PROFILE_STEP: (state, updatedStep) => {
          state.businessProfileSteps[updatedStep.name] = updatedStep;
        },
        UPDATE_PAYMENTS_STEP: (state, updatedStep) => {
          state.steps[updatedStep.name] = updatedStep;
        },
        UPDATE_PAYMENTS_ONBOARDING_STEP: (state, updatedStep) => {
          state.paymentsOnboardingSteps[updatedStep.name] = updatedStep;
        },
        UPDATE_INVITED_USER_ONBOARDING_STEP: (state, updatedStep) => {
          state.invitedUserOnboardingSteps[updatedStep.name] = updatedStep;
        },
        RESET_SELECTED_BILL: (state) => {
          state.selectedBill = {};
          state.selectedBillBeneficiary = {};
        },
        UPDATE_SELECTED_BILL: (state, updatedBill) => {
          state.selectedBill = updatedBill;
        },
        UPDATE_BILL_FLOW_TYPE: (state, value) => {
          state.billFlowType = value;
        },
        RESET_FILTERED_REPAYMENTS: (state) => {
          state.filteredRepayments = {};
        },
        ADD_BENEFICIARY_FORM_FIELD: (state, payload) => {
          state.beneficiaryFormInfo = {
            ...state.beneficiaryFormInfo,
            ...payload,
          };
        },
        RESET_BENEFICIARY_FORM_FIELD: (state) => {
          state.beneficiaryFormInfo = {};
        },
        EMAIL_VERIFIED: (state) => {
          state.emailVerified = true;
        },
      },
    },
  ),
};
